import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@lagoon/ui";

import { sortOrgs, type Org } from "../orgs.js";

/**
 * Which organisation the dashboard is looking at.
 *
 * A one-of-many choice, so it stays a select. It is now the design library's
 * Radix select rather than a bare `<select>`, which changes nothing about the
 * semantics that mattered: the trigger is a real button carrying
 * `aria-label`, the list is a listbox, and the keyboard behaviour is the
 * platform's own. What it buys is the brand's field, one popover style shared
 * with every other menu in the product, and a value that can be rendered as
 * more than a string.
 *
 * Nothing is rendered while there is only one organisation, which is everybody
 * until somebody makes a team. A picker with a single option teaches the reader
 * nothing and costs them a glance every time the page loads. Creating the
 * second one lives on the Members screen, where the rest of "who is in this
 * organisation" already is.
 *
 * The ordering is `sortOrgs`, not the API's: personal first, then teams by
 * name. The API returns newest first, which is meaningless to everybody except
 * the person who did the creating.
 */
export function OrgSwitcher(props: {
  orgs: readonly Org[];
  active: Org;
  onSelect: (orgId: string) => void;
}): React.ReactElement | null {
  if (props.orgs.length <= 1) return null;

  return (
    <Select value={props.active.id} onValueChange={props.onSelect}>
      <SelectTrigger aria-label="Organisation" className="lg:max-w-[240px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {sortOrgs(props.orgs).map((org) => (
          <SelectItem key={org.id} value={org.id}>
            {org.kind === "personal" ? `${org.name} (personal)` : org.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
